import { ExportedTree, TreeNode } from "@/types/tree";
import {
  DEFAULT_NEXT_NODE_ID,
  exportTree,
  findNode,
  getBlankTree,
  isDescendant,
  treesEqual
} from "@/lib/tree/tree";
import { configStore, documentStore, uiStore } from "@/store/store";
import { GlobalBlock } from "@/types/global-blocks";
import defensiveCopy from "@/util/defensive-copy";
import {
  getBlockFromWordPress,
  saveGlobalBlockToWordPress
} from "@/lib/global-blocks/global-blocks";
import { breakdanceUndo } from "@/lib/undo/undo";
import { pipe } from "fp-ts/function";
import { fold } from "fp-ts/Either";
import {
  copyBlockNodesIntoInlineEditingElement,
  getNodesFromInlineEditingElement,
  deleteChildren
} from "./tree";
import { BuilderElement } from "@/types/elements";
import { blurContentEditableElement } from "@/util/blur-content-editable";
import { inlineEditorActions } from "./actions-api";

// API docs: https://github.com/soflyy/breakdance/pull/8172#issuecomment-3226661140

export async function startInlineEditing(options: {
  elementId: TreeNode["id"];
  blockId: number;
  loadFreshBlock?: boolean;
  scrollToElementOnStart?: boolean;
  showBackdrop?: boolean;
  escToSelectElement?: boolean;
  selectElementOnClose?: boolean;
  forceSsrOnClose?: boolean;
}) {
  if (uiStore.elementsThatAreUpdating[options.elementId]) {
    console.log(
      "Element is updating, wait for it to finish before starting a new inline editing session."
    );
    return;
  }

  if (uiStore.isInlineEditing) {
    console.log(
      "Inline editing already in progress, end the current session first."
    );
    return;
  }

  const {
    elementId,
    blockId,
    showBackdrop = true,
    escToSelectElement = true,
    selectElementOnClose = true,
    scrollToElementOnStart = true,
    ...restOptions
  } = options;

  const context = {
    elementId,
    blockId,
    showBackdrop,
    escToSelectElement,
    loading: true,
    selectElementOnClose,
    scrollToElementOnStart,
    ...restOptions
  };

  uiStore.setInlineEditingContext(context);

  const block = await loadBlockIfNeeded(blockId, restOptions.loadFreshBlock);

  if (!block) {
    console.log("Block not found, this should never happen.");
    console.trace();
    return;
  }

  breakdanceUndo.transaction(
    () => {
      uiStore.setInlineEditingContext({
        ...context,
        loading: false,
        block
      });

      copyBlockNodesIntoInlineEditingElement(block, elementId);
    },
    "Start Inline Editing",
    `inline-editing:${elementId}`
  );
}

export async function saveInlineEditing(elementId: TreeNode["id"]) {
  const element = findNode(documentStore.document.tree, elementId);
  if (!element) return false;

  const block = uiStore.inlineEditingContext?.block;

  if (!block) {
    console.log("Block not found, this should never happen.");
    console.log(uiStore.inlineEditingContext);
    console.trace();
    return false;
  }

  const nodes = getNodesFromInlineEditingElement(element);

  if (!nodes.length) {
    console.log("Nothing to save, this element is empty.");
    return false;
  }

  return updateBlockTree(block, elementId, nodes);
}

// Closing does not save the changes, it just discards the temporary tree, save first if you want to keep them.
export function closeInlineEditor(elementId: TreeNode["id"]) {
  const element = findNode(documentStore.document.tree, elementId);

  if (element) {
    inlineEditorActions.run.onBeforeClosingInlineEditor({
      elementId,
      childrenIds: element.children.map(child => child.id)
    });
  }

  breakdanceUndo.transaction(
    () => {
      if (element) {
        deleteChildren(element);
      }

      uiStore.setInlineEditingContext(null);
      uiStore.setInlineEditingRect(null);
    },
    "Stop Inline Editing",
    `inline-editing:stop:${elementId}`
  );
}

/**
 * Ends the inline editing session:
 * - Saves edits to WordPress
 * - Clears the editing context
 * - Forces an SSR re-render
 * - Re-selects the Global Block element to prevent the user from being stuck in the Inline Editor.
 */
export async function stopInlineEditing() {
  const context = uiStore.inlineEditingContext;
  const elementId = context?.elementId;
  const selectElementOnClose = context?.selectElementOnClose;
  const forceSsrOnClose = context?.forceSsrOnClose;

  if (!elementId) return;

  // This ensures any changes in the inline edited element are saved.
  blurContentEditableElement();

  const success = await saveInlineEditing(elementId);

  closeInlineEditor(elementId);

  /*
  Trigger a re-render of the SSR element
  Note: Only trigger a SSR re-render after closeInlineEditor has been called,
  because the SSR code expects the string "%%SSR%%" to be present in the template.
  */
  if (success || forceSsrOnClose) {
    uiStore.setRefreshSsrElementId(elementId);
  }

  // Reset the active element to the element that was being edited, to prevent the user from being stuck in the inline editor.
  if (selectElementOnClose) {
    uiStore.activateElement(elementId);
  }
}

async function loadBlockIfNeeded(blockId: number, loadFreshBlock = false) {
  let block = configStore.globalBlocks.find(block => block.id === blockId);

  if (!block || loadFreshBlock) {
    block = pipe(
      await getBlockFromWordPress(blockId),
      fold(
        () => undefined,
        block => block
      )
    );
  }

  return block;
}

export async function updateBlockTree(
  block: GlobalBlock,
  elementId: TreeNode["id"],
  children: TreeNode[]
) {
  const blankTree = getBlankTree();

  const updatedTree = block?.tree
    ? defensiveCopy({
        ...block.tree,
        root: {
          ...block.tree.root,
          children
        }
      })
    : exportTree({
        ...blankTree,
        root: {
          ...blankTree.root,
          children
        }
      });

  if (!shouldBlockBeUpdated(block, updatedTree)) {
    console.log("Block is up to date, no need to update.");
    return;
  }

  // Show loading state
  uiStore.setElementUpdatingState({
    elementId,
    isUpdating: true
  });

  // Update blocks in store
  const newBlocks = configStore.globalBlocks.map(b =>
    b.id === block.id ? { ...b, tree: updatedTree } : b
  );

  configStore.setGlobalBlocks(newBlocks);

  // And save to WordPress
  const result = await saveGlobalBlockToWordPress({
    ...block,
    tree: updatedTree
  });

  uiStore.setElementUpdatingState({
    elementId,
    isUpdating: false
  });

  return result;
}

export function getInlineBlockPreviewableItemUrl() {
  if (!uiStore.inlineEditingContext?.blockId) return null;

  const block = configStore.globalBlocks.find(
    block => block.id === uiStore.inlineEditingContext?.blockId
  );

  return block?.lastPreviewedItem !== false
    ? block?.lastPreviewedItem?.url
    : null;
}

export function elementIsInsideInlineEditingContext(elementId: TreeNode["id"]) {
  if (!uiStore.inlineEditingContext) return false;

  const inlineEditingContextElementId = uiStore.inlineEditingContext.elementId;

  if (!inlineEditingContextElementId) return false;

  const isInside = isDescendant(
    documentStore.document.tree,
    inlineEditingContextElementId,
    elementId
  );

  return isInside;
}

export function getInlineEditingNextNodeId() {
  const blockId = uiStore.inlineEditingContext?.blockId;
  const tree = uiStore.inlineEditingContext?.block?.tree;

  if (blockId && tree) {
    return tree._nextNodeId;
  }

  return DEFAULT_NEXT_NODE_ID;
}

export function setInlineEditingNextNodeId(nextNodeId: number) {
  const context = uiStore.inlineEditingContext;
  const block = context?.block;

  if (!block) return;

  const tree = block.tree ? block.tree : exportTree(getBlankTree());

  uiStore.setInlineEditingContext({
    ...context,
    block: {
      ...block,
      tree: {
        ...tree,
        _nextNodeId: nextNodeId
      }
    }
  });
}

export function shouldBlockBeUpdated(
  block: GlobalBlock,
  updatedTree: ExportedTree
) {
  if (!block.tree) return true;
  return !treesEqual(updatedTree, block.tree);
}

export function shouldActivateInlineElementOnEscPress() {
  return (
    uiStore.inlineEditingContext?.escToSelectElement &&
    uiStore.inlineEditingContext?.elementId
  );
}

export function shouldForceElementToBeContainer(element: BuilderElement) {
  return uiStore.isInlineEditing && element.nestingRule.inlineEditable;
}

export function isElementInlineEditable(element: BuilderElement) {
  return (
    element.nestingRule.inlineEditable &&
    element.nestingRule.inlineEditableBlockPath
  );
}
